import React, { useCallback, useState } from 'react';
import StorageSelector from '../../../components/storage/StorageSelector';
import ArticleSelector from '../../../components/article/ArticleSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { usePostStockCorrectionMutation } from '../../../redux/api/stockApi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useApiErrorToast } from '../../../hooks/useApiError';

const StockCorrection = () => {
  const [storageLocation, setStorageLocation] = useState(null);
  const [direction, setDirection] = useState('decrease');
  const [reason, setReason] = useState('');
  const [articlesToBook, setArticlesToBook] = useState([]);
  const [postCorrection, { isLoading }] = usePostStockCorrectionMutation();
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();

  const handleSetArticlesToBook = useCallback((newArticles) => {
    setArticlesToBook(newArticles);
  }, []);

  const removeLine = (indexToRemove) => {
    setArticlesToBook((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleChangeQuantity = (event, index) => {
    const newQuantity = parseInt(event.target.value) || 0;
    setArticlesToBook((prev) => {
      const updated = [...prev];
      updated[index].quantity = newQuantity;
      return updated;
    });
  };

  const validLines = articlesToBook.filter((line) => line.quantity > 0);
  const canSubmit =
    !!storageLocation && reason.trim().length > 0 && validLines.length > 0;

  const handleSubmit = async () => {
    const postingObj = {
      storage_id: storageLocation,
      direction,
      reason: reason.trim(),
      items: validLines.map((line) => ({
        article_id: line.article.id,
        quantity: line.quantity,
      })),
    };
    try {
      await postCorrection(postingObj).unwrap();
      toast.success('Bestandskorrektur gebucht!');
      navigate('/stock');
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Buchen der Bestandskorrektur');
    }
  };

  return (
    <div>
      <h3>Bestandskorrektur</h3>
      <p className='text-muted'>
        Manuelle Korrektur des Bestands an einem Lagerort (z.&nbsp;B. zur
        Berichtigung eines fehlerhaften Wareneingangs). Jede Korrektur wird mit
        Grund protokolliert.
      </p>

      <div className='row mt-4'>
        <div className='col-3'>
          <label htmlFor='storageLocation' className='col-form-label'>
            Lagerort
          </label>
        </div>
        <div className='col-3'>
          <StorageSelector
            storageLocation={storageLocation}
            setStorageLocation={setStorageLocation}
          />
        </div>
      </div>

      <div className='row mt-3'>
        <div className='col-3'>
          <label className='col-form-label'>Richtung</label>
        </div>
        <div className='col-6'>
          <div className='btn-group' role='group'>
            <input
              type='radio'
              className='btn-check'
              name='direction'
              id='direction-decrease'
              checked={direction === 'decrease'}
              onChange={() => setDirection('decrease')}
            />
            <label
              className='btn btn-outline-danger'
              htmlFor='direction-decrease'
            >
              Bestand reduzieren
            </label>
            <input
              type='radio'
              className='btn-check'
              name='direction'
              id='direction-increase'
              checked={direction === 'increase'}
              onChange={() => setDirection('increase')}
            />
            <label
              className='btn btn-outline-success'
              htmlFor='direction-increase'
            >
              Bestand erhöhen
            </label>
          </div>
        </div>
      </div>

      <div className='row mt-3'>
        <div className='col-3'>
          <label htmlFor='reason' className='col-form-label'>
            Grund <span className='text-danger'>*</span>
          </label>
        </div>
        <div className='col-6'>
          <input
            id='reason'
            type='text'
            className='form-control'
            value={reason}
            maxLength={255}
            placeholder='z.B. Inventurkorrektur, Bruch, Fehlbuchung'
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>

      <div className='row mt-3'>
        <div className='col-3'>
          <label htmlFor='article' className='col-form-label'>
            Artikel hinzufügen
          </label>
        </div>
        <div className='col-3'>
          <ArticleSelector
            articlesToBook={articlesToBook}
            setArticlesToBook={handleSetArticlesToBook}
          />
        </div>
      </div>

      <div className='mt-5'></div>
      {articlesToBook.length > 0 ? (
        <>
          <h4>Zu korrigierende Artikel:</h4>
          <table className='table table-striped mt-3 align-middle'>
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>Artikel</th>
                <th>Gebinde</th>
                <th>Menge</th>
              </tr>
            </thead>
            <tbody>
              {articlesToBook.map((line, index) => (
                <tr key={index}>
                  <td>
                    <button
                      className='btn btn-sm ps-0 text-danger border-0'
                      onClick={() => removeLine(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                  <td>{line.article.name}</td>
                  <td>{line.article.unit.display_name}</td>
                  <td>
                    <input
                      className='form-control'
                      type='number'
                      min={1}
                      value={line.quantity}
                      onChange={(event) => handleChangeQuantity(event, index)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            className='btn btn-primary'
            onClick={handleSubmit}
            disabled={isLoading || !canSubmit}
          >
            Korrektur buchen
          </button>
        </>
      ) : null}
    </div>
  );
};

export default StockCorrection;
